<template>
  <div class="image-picker mb-5">
    <div class="input-slot">
      <v-text-field
        data-cy="image-picker-input"
        :label="label"
        :hint="hint"
        :persistent-hint="true"
        class="image-picker mb-5"
        :value="imageUrl"
        readonly
      />
      <v-col class="container">
        <v-img
          v-if="imageId"
          :src="imageUrl"
          :lazy-src="showLazy ? 'https://picsum.photos/128/?blur=5' : undefined"
          contain
          min-height="200"
          max-height="250"
        />
        <v-row class="mt-5 pb-2" justify="space-around">
          <v-btn
            id="imagePickerRandomButton"
            :aria-label="t('aria.label.randomizeImage')"
            data-cy="image-picker-btn-random"
            class="button"
            :disabled="!images || images.length === 0"
            @click="random"
          >
            {{ t('components.imagePicker.random') }}
          </v-btn>
          <v-dialog
            v-model="dialog"
            data-cy="image-picker-dialog"
            transition="slide-y-transition"
            scrollable
            max-width="800px"
          >
            <template #activator="{ props }">
              <v-btn
                id="imagePickerChoiceButton"
                :aria-label="t('aria.label.selectImage')"
                data-cy="image-picker-btn-pick"
                class="button"
                color="primary"
                :disabled="!images || images.length === 0"
                v-bind="props"
              >
                {{ t('components.imagePicker.pick') }}
              </v-btn>
            </template>
            <image-picker-view
              v-model="imageId"
              data-cy="image-picker-view-close-btn"
              :images="images"
              :categories="categories"
              @close="dialog = false"
            />
          </v-dialog>
        </v-row>
        <v-progress-linear indeterminate :active="loading" />
      </v-col>
    </div>
  </div>
</template>

<script lang="ts">
import ImagePickerView from '@/views/image/ImagePickerView.vue';
import { defineComponent, ref, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { DonationImage } from '@/interfaces/donation/donationImage';

export default defineComponent({
  components: { ImagePickerView },
  props: {
    modelValue: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    images: {
      type: Array,
      default: null,
    },
    categories: {
      type: Array,
      default: null,
    },
    showLazy: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const dialog = ref(false);
    const { t } = useLabel();
    const loading = ref(false);
    const imageId = ref(props.modelValue);

    const imageUrl = computed(() => {
      const images = props.images as DonationImage[] | null;
      const image = images?.find((i) => i.id === imageId.value);
      return image?.url;
    });

    watch(
      imageId,
      (val) => {
        // Always propagate selected image via the v-model (update:modelValue)
        emit('update:modelValue', val);
      },
      { immediate: true }
    );

    const random = () => {
      const images = props.images as DonationImage[];
      if (!images || images.length === 0) return;
      loading.value = true;
      const newId = images[Math.floor(Math.random() * images.length)].id;
      if (newId != imageId.value) {
        imageId.value = newId;
      } else {
        random();
      }
      loading.value = false;
    };

    return {
      t,
      dialog,
      loading,
      imageId,
      imageUrl,
      random,
    };
  },
});
</script>
<style>
.image-picker .container {
  padding: 0 !important;
}

.image-picker .v-input__slot,
.image-picker .input-slot {
  flex-direction: column;
  align-items: start;
}
</style>
<style scoped>
.container {
  width: 100%;
  flex-shrink: 0;
  padding: 0 !important;
}

.image {
  background-color: grey;
  height: 200px;
  width: 100%;
}
</style>
